import {
    useEligibleForJiraMadlibSetup,
    useIsDesktop,
    useIsConfluenceContactCompanyExpandEligible,
    useIsJiraContactCompanyExpandEligible,
    useIsStandardToPremiumUpgradeEligible,
    useLoggedIn,
    useLoggedOut,
    useLoggedInHasJSM,
    useLoggedOutHasExistingSites,
    useNewUser,
    usePrefersDarkColorScheme,
    useRovoEnabledCohort,
    useSpecifiedPathnames,
    useEligibleForD0JTBDOnboarding,
    useIsSoftwareConfluencePath,
    useIsSoftwareJiraPath
} from "@/components/user-context";

import { useEnglishLocale } from "../sentry-intl";

import { Manifest } from ".";

const booleanTypeGuard = (value: unknown): value is boolean =>
    typeof value === "boolean";

export const experiments = {
    gwp9973JSMBackToProductHero: {
        experimentName: "jsm_back_to_product_hero",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedInHasJSM() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "Back to product hero on JSM product page for logged-in users with a JSM site",
            expectedReleaseDate: "04/16/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: ["/software/jira/service-management"]
            }
        }
    },
    gwp8911NotificationBell: {
        experimentName: "wac_notification_bell_experiment",
        parameterName: "cohort",
        defaultValue: "control",
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedIn() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "notification bell in global nav for logged-in desktop users",
            expectedReleaseDate: "01/16/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: [
                    "/",
                    "/software/jira",
                    "/software/jira/service-management",
                    "/software/confluence",
                    "/software/jira/pricing",
                    "/software/confluence/templates",
                    "/software/confluence/pricing",
                    "/software/jira/product-discovery"
                ]
            }
        }
    },
    gwp9322WacHomepageRefresh: {
        experimentName: "gwp-9322-wac-homepage-refresh-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Teamwork Collection refresh changes on homepage",
            expectedReleaseDate: "01/15/2026",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/"]
            }
        }
    },
    cfind4782LandingPageSubAnchorV3: {
        experimentName: "cfind-4782-landing-page-sub-anchor-v3",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test if personalizing the landing page off the sub anchor will increase d1to6",
            expectedReleaseDate: "07/14/2025",
            expectedRuntime: "6 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraOnboardingAiMadlibV2: {
        experimentName: "jira-onboarding-ai-madlib-v2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useEligibleForJiraMadlibSetup
        },
        metadata: {
            description:
                "Enable AI Madlib during Jira onboarding experience V2 expanded iteration with all team types",
            expectedReleaseDate: "2/27/2026",
            expectedRuntime: "30 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfind1504AfterD7GetPaidUsersBackToProduct: {
        experimentName: "jfind-1504-after-d7-get-users-back-to-product",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Paid: Display Get Users Back to Product screen for users who have one Jira after D7 or multiple Jiras",
            expectedReleaseDate: "06/05/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jiraProjectOnboardingExtraFieldsForWizards: {
        experimentName: "jira_onboarding_enable_extra_fields_jsw_projects",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Allow all project creations through the wizard to have extra fields on their issue types",
            expectedReleaseDate: "02/06/2025",
            expectedRuntime: "5 weeks",
            context: {
                urls: []
            }
        }
    },
    jfindSimplifiedListViewWithBasicTasks: {
        experimentName: "jfind-simplified-list-view-with-basic-tasks",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Simplifies the list view columns and adds basic sample tasks to the initial project",
            expectedReleaseDate: "17/06/2025",
            expectedRuntime: "131 days",
            context: {
                urls: []
            }
        }
    },
    jfindBasicSampleTasksForScrumOrSoftwareKanban: {
        experimentName: "jira-create-generic-tasks-and-minimize-quickstart",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Adds basic sample tasks to the initial project for Scrum or Software Kanban projects",
            expectedReleaseDate: "22/09/2025",
            expectedRuntime: "37 days",
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardDevKanban: {
        experimentName: "jira_software_kanban_wizard_m2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for dev kanban while provisioning is taking place",
            expectedReleaseDate: "21/03/2024",
            expectedRuntime: "7 weeks",
            context: {
                urls: []
            }
        }
    },
    signupAddPaymentAb: {
        experimentName: "signup_trialing_add_payment_ab",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            // we need to fire our exposure event manually because we can't determine if the user is eligible from here
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment to enable users with valid sites to go to payment at the sign-up screen",
            expectedReleaseDate: "02/27/25",
            expectedRuntime: "TBD",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerConfluence: {
        experimentName: "gwp-5090-experiment-banner-confluence",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Service Management product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    confluenceContactAndCompanyExpandSiteActivation: {
        experimentName: "confluence-contact-company-expand-site-activation",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsConfluenceContactCompanyExpandEligible
        },
        metadata: {
            description:
                "Prevent accidental site activations by sending users to existing products",
            expectedReleaseDate: "01/30/26",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/confluence", "/try/cloud/signup"]
            }
        }
    },
    jiraContactAndCompanyExpandSiteActivation: {
        experimentName: "jira-contact-company-expand-site-activation",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsJiraContactCompanyExpandEligible
        },
        metadata: {
            description:
                "Prevent accidental site creations by sending users to existing products",
            expectedReleaseDate: "01/30/26",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/jira", "/try/cloud/signup"]
            }
        }
    },
    gwp7617JiraShortTermAiInfusionExperiment: {
        experimentName: "gwp-7617-jira-short-term-ai-infusion-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Short term AI",
            expectedReleaseDate: "08/21/25",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jfindMatureExampleProjectExperiment: {
        experimentName: "jfind-mature-example-project",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useNewUser
        },
        metadata: {
            description:
                "Supports creating a new example project for Jira customers",
            expectedReleaseDate: "06/02/2025",
            expectedRuntime: "13 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwp4948GartnerJiraProductTourExperiment: {
        experimentName: "gwp-4948-gartner-jira-product-tour-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "gartner section on jira product tour",
            expectedReleaseDate: "April 2025",
            expectedRuntime: "2 weeks",
            clientOnly: true,
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    gwp6441ConfluenceTemplatesProductNavCta: {
        experimentName: "gwp-6441-confluence-templates-product-nav-cta",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return !useLoggedOut();
            }
        },
        metadata: {
            description: "Confluence Templates Product Nav CTA experiment",
            expectedReleaseDate: "undefined",
            expectedRuntime: "77 days",
            clientOnly: true,
            context: {
                urls: ["/software/confluence/templates/*"]
            }
        }
    },
    gwp4871JiraTemplatesProductNavCta: {
        experimentName: "gwp-4871-jira-templates-product-nav-cta",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Jira Software Templates Pages Product Nav CTA Experiment",
            expectedReleaseDate: "09/12/2025",
            expectedRuntime: "38 days",
            context: {
                urls: ["/software/jira/templates/*"]
            }
        }
    },
    jiraOnboardingAutodetectSystemTheme: {
        experimentName: "jira-onboarding-autodetect-system-theme",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: usePrefersDarkColorScheme
        },
        metadata: {
            description:
                "Autodetect system theme and enroll users into experiment",
            expectedReleaseDate: "08/27/2025",
            expectedRuntime: "18 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfindOnboardingWizardInviteStep: {
        experimentName: "jfind-add-invite-step-to-onboarding-wizard",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Add invite step to the Jira onboarding wizard",
            expectedReleaseDate: "09/12/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    twcg990D0JTBDOnboarding: {
        experimentName: "twcg-990_d0_jtbd_onboarding",
        parameterName: "cohort",
        defaultValue: "not-enrolled" as
            | "not-enrolled"
            | "control"
            | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: useEligibleForD0JTBDOnboarding
        },
        metadata: {
            description: "D0 JTBD Onboarding Experiment For TWC",
            expectedReleaseDate: "", // to be updated when known
            expectedRuntime: "", // to be updated when known
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfind1968teamTypeSpaceRecommendation: {
        experimentName: "jfind-1968-team-type-and-space-recommendation",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Space name recommendation based on team types",
            expectedReleaseDate: "10/14/2025",
            expectedRuntime: "1 week",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwpAJContextualBanditsJiraBPS: {
        experimentName: "gwp-aj-contextual-bandits-jira-bps",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/jira"]) &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale()
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Jira BPS v2",
            expectedReleaseDate: "02/13/2026",
            expectedRuntime: "11 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },

    wacRoutingTableMvpPerf: {
        experimentName: "wac-routing-table-mvp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measure performance degredation of wac-routing-table in wac-web",
            expectedReleaseDate: "08/28/2025",
            expectedRuntime: "5 days",
            context: {
                urls: ["/software/jira/enterprise"]
            }
        }
    },
    confluencePricingPageWaeMigration: {
        experimentName: "gwp-7750-confluence-pricing-page-wae-migration-exp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Migration of Confluence pricing page to WAE",
            expectedReleaseDate: "08/26/2025",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/confluence/pricing"]
            }
        }
    },
    wacAISearchSummaryV2: {
        experimentName: "wac-ai-search-summary-v2",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useEnglishLocale() && useIsDesktop() && useLoggedIn();
            }
        },
        metadata: {
            description: "AI Search Summary Experiment V2",
            expectedReleaseDate: "N/A",
            expectedRuntime: "N/A",
            context: {
                // eslint-disable-next-line local/warn-wildcard-usage-eslint
                urls: ["*"]
            }
        }
    },
    gwp8058GeneralSigninPromptForExistingUsers: {
        experimentName: "gwp-8058-general-signin-prompt-for-existing-users",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedOutHasExistingSites() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "General signin prompt for existing users for users home homepage",
            expectedReleaseDate: "10/20/2025",
            expectedRuntime: "8 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    gwp8288PersonalizingWacSoftwarePage: {
        experimentName: "gwp-8288-personalizing-wac-software-page",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Personalizing WAC Software Page Experiment",
            expectedReleaseDate: "10/30/2025",
            expectedRuntime: "6 weeks",
            context: {
                urls: ["/software"]
            }
        }
    },
    jsmGrowth220JsmJpdWacSignup: {
        experimentName: "jsmgrowth-220_jsm_jpd_wac_signup",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedIn() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "Show IT Service Management template option for JSM eligible customers in Template Choice View",
            expectedReleaseDate: "04/12/2025",
            expectedRuntime: "53 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    twcg790ContextualBackground: {
        experimentName: "twcg-790-contextual-backgrounds-wac-sign-in",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "adding atlOrigin and application query strings to IDAC login URL to enable contextual background on sign in page",
            expectedReleaseDate: "01/12/2025",
            expectedRuntime: "7 weeks",
            clientOnly: true,
            context: {
                // eslint-disable-next-line
                urls: ["*"]
            }
        }
    },
    twcg907Astro540WacUpgradeS2p: {
        experimentName: "twcg-907-astro-540-wac-upgrade-s2p",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: useIsStandardToPremiumUpgradeEligible
        },
        metadata: {
            description: "Allow Standard to Premium Upgrades on Signup Page",
            expectedReleaseDate: "11/24/2025",
            expectedRuntime: "90 days",
            clientOnly: true,
            context: {
                urls: ["/try/cloud/signup"]
            }
        }
    },
    wacRedirectEditionParityAB: {
        experimentName: "wac_redirect_edition_parity_ab",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "AB test for enabling WAC redirect edition parity",
            expectedReleaseDate: "17/12/2025",
            expectedRuntime: "100 days",
            clientOnly: true,
            context: {
                urls: ["/try/cloud/signup"]
            }
        }
    },
    gwp8979RovoChatOnWac: {
        experimentName: "gwp-8979-rovo-chat-on-wac",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useRovoEnabledCohort
        },
        metadata: {
            description: "Rovo Chat on WAC",
            expectedReleaseDate: "11/30/2025",
            expectedRuntime: "30 days",
            context: {
                urls: ["/software/rovo"]
            }
        }
    },
    dxpMigrationSoftwarePage: {
        experimentName: "dxp-migration-software-page",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "DXP Migration Software Page Experiment",
            expectedReleaseDate: "12/05/2025",
            expectedRuntime: "7 days",
            context: {
                urls: ["/software"]
            }
        }
    },
    jiraPricingWaeMigrationExperimentRerunExp: {
        experimentName: "jira_pricing_wae_migration_experiment_rerun_exp",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Rerun of migration of Jira pricing page to WAE",
            expectedReleaseDate: "12/12/2025",
            expectedRuntime: "43 days",
            context: {
                urls: ["/software/jira/pricing"]
            }
        }
    },
    gwpAJContextualBanditsConfluenceSubAnchor: {
        experimentName: "gwp-aj-contextual-bandits-confluence-subanchor",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/confluence"]) &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale()
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Confluence sub anchor",
            expectedReleaseDate: "12/19/2025",
            expectedRuntime: "12 weeks",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    gwp9508ConfluenceBannerIosNativeApp: {
        experimentName: "gwp-9508-confluence-banner-ios-native-app",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsSoftwareConfluencePath
        },
        metadata: {
            description: "Confluence Banner Ios Native App Experiment",
            expectedReleaseDate: "01/22/2026",
            expectedRuntime: "39 days",
            context: {
                urls: ["/software/confluence/*"]
            }
        }
    },
    gwp9507JiraBannerIosNativeApp: {
        experimentName: "gwp-9507-jira-banner-ios-native-app",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsSoftwareJiraPath
        },
        metadata: {
            description: "Jira Banner App Experiment",
            expectedReleaseDate: "01/22/2026",
            expectedRuntime: "60 days",
            context: {
                urls: ["/software/jira/*"]
            }
        }
    },
    cfind4666JiraFfl: {
        experimentName: "cfind-4666-frictionless-first-look-jira",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Enable paid customers on WAC to describe their JTBD in natural language and generate a personalized preview of JIRA",
            expectedReleaseDate: "02/17/2026",
            expectedRuntime: "",
            context: {
                urls: ["/software/jira"]
            }
        }
    }
} satisfies Manifest<unknown>;
